import {
  VehicleRegistry,
  createEventBus,
  type EventBus,
  type LinkStateEvent,
  type Unsubscribe,
  type VehicleState,
  type VehicleStateEvent,
} from '@hgc/core';
import type { LinkState, LinkStats, MavlinkSession, Transport, TransportKind } from '@hgc/link';

import { Signal } from './Signal';
import { VehicleRoster, EMPTY_VEHICLE_LIST, type VehicleSummary } from './VehicleRoster';
import { createTransport, type LinkConfig } from './linkConfig';
import { LINK_PHASE_OF_STATE, describeLink, type LinkPhase } from './linkStatus';

/** 最近一次失败。界面上"为什么连不上"的答案就存在这里，而不是散在控制台里 */
export interface LinkErrorReport {
  readonly message: string;
  readonly at: number;
  readonly config: LinkConfig | null;
}

/**
 * 链路对外的一份完整快照。
 *
 * 整块替换（`Object.freeze`）而不是就地改字段：界面上的消费方拿到的引用变了就是"该重画了"，
 * 这个判断不需要知道到底是 phase 变了还是 reason 变了。
 */
export interface LinkSnapshot {
  readonly phase: LinkPhase;
  /** 会话层的细分状态（`LinkState`）。界面一般不直接用它，但它要能在诊断里读到 */
  readonly linkState: LinkState;
  readonly title: string;
  readonly level: 'neutral' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';
  /** 为什么现在不是正常连接；连上了就是 null */
  readonly reason: string | null;
  /** 当前（或最近一次尝试的）链路配置 */
  readonly config: LinkConfig | null;
  readonly transportKind: TransportKind | null;
  /** 传输自报的可读标识，例如 `模拟机 · 1 架 多旋翼` */
  readonly transportLabel: string | null;
  /** 进入当前阶段的时刻 */
  readonly since: number;
}

export type LinkManagerEvents = {
  /** 快照变化（与 React 侧的 Signal 同源，供非 React 消费者使用） */
  snapshot: LinkSnapshot;
  error: LinkErrorReport;
};

export interface LinkManagerOptions {
  readonly now?: () => number;
  /**
   * 传输工厂。默认按 `LinkConfig` 造真实传输；测试可注入受控实现来构造
   * "打开失败/中途断开"这类真机上不好复现的场景。
   */
  readonly createTransport?: (config: LinkConfig) => Transport;
  /** 链路统计的最短刷新间隔。帧率可以到 20Hz，但统计不需要那么多次重渲染 */
  readonly statsIntervalMs?: number;
  readonly onListenerError?: (error: unknown, type: string) => void;
}

const DEFAULT_STATS_INTERVAL_MS = 250;

/**
 * 链路管理器：把 transport → 会话 → 车辆注册表 这条**运行时**装配起来，并对外暴露
 * 可以与 React 无关地存活的状态。
 *
 * 为什么它必须活在 React 之外（这是本文件最重要的设计约束）：
 * 生命周期的归属决定了切分区时会发生什么。若连接由某个组件的 `useEffect` 建立，
 * 那么"飞行 → 设置 → 回飞行"就会走一遍 断开→重连；对真机意味着重新握手、
 * 重新协商消息频率、丢掉这期间所有遥测。所以：**组件只订阅，不拥有**。
 * 谁拥有？`AppStore` 里的这一个实例，它在路由切换、组件卸载时都不受影响。
 *
 * 另一个决定：注册表**每次连接都新建一个**。一条链路对应一个会话、一套 `sysid:compid` 归集，
 * 复用上一个注册表会让"新链路的第一辆车"继承旧链路的身份与状态 —— 那种脏数据
 * 只有在换机器的现场才会被发现。
 */
export class LinkManager {
  readonly events: EventBus<LinkManagerEvents>;

  private readonly now: () => number;
  private readonly factory: (config: LinkConfig) => Transport;
  private readonly statsIntervalMs: number;

  private readonly statusValue = new Signal<LinkSnapshot>({
    phase: 'disconnected',
    linkState: 'idle',
    title: '未连接',
    level: 'neutral',
    reason: null,
    config: null,
    transportKind: null,
    transportLabel: null,
    // 真实时刻在构造函数里补上：字段初始化器先于构造函数体执行，这里拿不到时钟
    since: 0,
  });
  private readonly statsValue = new Signal<LinkStats | null>(null);
  private readonly vehiclesValue = new Signal<readonly VehicleSummary[]>(EMPTY_VEHICLE_LIST);
  private readonly primaryStateValue = new Signal<VehicleState | null>(null);

  private currentRegistry: VehicleRegistry | null = null;
  private currentTransport: Transport | null = null;
  private roster: VehicleRoster | null = null;
  private readonly offs: Unsubscribe[] = [];
  private lastErrorReport: LinkErrorReport | null = null;
  private lastStatsAt = 0;
  /** 每秒强制刷新一次链路统计。帧驱动不够：断链之后再没有帧，而"静默多久了"恰恰在那时才要紧 */
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  constructor(options: LinkManagerOptions = {}) {
    this.now = options.now ?? (() => Date.now());
    this.factory = options.createTransport ?? createTransport;
    this.statsIntervalMs = options.statsIntervalMs ?? DEFAULT_STATS_INTERVAL_MS;
    this.events = createEventBus<LinkManagerEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );
    this.statusValue.set({ ...this.statusValue.get(), since: this.now() });
  }

  // -------------------------------------------------------------------------
  // React 边界（全部是稳定引用，可直接进 useSyncExternalStore）
  // -------------------------------------------------------------------------

  readonly subscribeStatus = this.statusValue.subscribe;
  readonly status = (): LinkSnapshot => this.statusValue.get();
  readonly subscribeStats = this.statsValue.subscribe;
  readonly stats = (): LinkStats | null => this.statsValue.get();
  readonly subscribeVehicles = this.vehiclesValue.subscribe;
  readonly vehicles = (): readonly VehicleSummary[] => this.vehiclesValue.get();
  readonly subscribePrimaryState = this.primaryStateValue.subscribe;
  readonly primaryState = (): VehicleState | null => this.primaryStateValue.get();
  readonly primaryVehicle = (): VehicleSummary | null =>
    this.vehiclesValue.get().find((vehicle) => vehicle.primary) ?? null;

  /** 订阅者总数。存在的唯一理由是泄漏检查：漏取消的表现是"越跑越卡"，没有任何报错 */
  listenerCount(): number {
    return (
      this.statusValue.listenerCount +
      this.statsValue.listenerCount +
      this.vehiclesValue.listenerCount +
      this.primaryStateValue.listenerCount +
      this.events.listenerCount()
    );
  }

  // -------------------------------------------------------------------------
  // 只读访问
  // -------------------------------------------------------------------------

  get registry(): VehicleRegistry | null {
    return this.currentRegistry;
  }

  get session(): MavlinkSession | null {
    return this.currentRegistry?.session ?? null;
  }

  get transport(): Transport | null {
    return this.currentTransport;
  }

  get error(): LinkErrorReport | null {
    return this.lastErrorReport;
  }

  // -------------------------------------------------------------------------
  // 连接 / 断开
  // -------------------------------------------------------------------------

  /**
   * 连接。**不抛错**，用返回值 + 快照里的 `reason` 报告结果。
   *
   * "连接失败"在这里是**预期结果**而不是异常：串口没有授权、浏览器不支持 Web Bluetooth
   * 都是日常会发生的事。让每个调用点（连接按钮、启动流程、将来的自动重连）都写一遍
   * try/catch，只会有一处漏写，而漏写的那次是界面白屏。
   */
  async connect(config: LinkConfig): Promise<boolean> {
    if (this.disposed) throw new Error('LinkManager 已销毁，不能再连接');

    await this.teardown();

    let transport: Transport;
    try {
      transport = this.factory(config);
    } catch (error) {
      const reason = messageOf(error, '创建传输通道失败，且底层没有给出原因');
      this.recordError(reason, config);
      this.updateStatus({
        phase: 'error',
        // 还没走到会话层，`linkState` 保持 idle —— 它描述的是会话状态，而这里没有会话
        linkState: 'idle',
        title: '链路失败',
        level: 'critical',
        reason,
        config,
        transportKind: config.kind,
        transportLabel: null,
      });
      return false;
    }

    // 时钟统一由本类下发：注入假时钟时，链路会话、车辆归集与界面看到的时刻必须同一个
    const registry = new VehicleRegistry({ session: { transport, now: this.now } });
    this.currentTransport = transport;
    this.currentRegistry = registry;
    this.roster = new VehicleRoster(registry, (list) => this.vehiclesValue.set(list));

    this.offs.push(
      registry.events.on('linkState', (change) => this.handleLinkState(change)),
      registry.events.on('state', (event) => this.handleVehicleState(event)),
      registry.events.on('primaryChanged', () => {
        this.primaryStateValue.set(registry.primary?.state ?? null);
      }),
    );

    this.statsTimer = setInterval(() => this.refreshStats(), 1000);

    this.updateStatus({
      phase: 'connecting',
      linkState: 'connecting',
      title: '连接中',
      level: 'info',
      reason: '正在打开链路…',
      config,
      transportKind: transport.kind,
      transportLabel: transport.label,
    });

    try {
      await registry.open();
    } catch (error) {
      const reason = this.failureReason(error, registry);
      const kind = transport.kind;
      const label = transport.label;
      this.recordError(reason, config);
      // 先退订再拆：拆的过程中会发出 `idle` 之类的事件，不该让正在失败的这一次连接
      // 被那些事件改写成"已断开"（用户看到的会是"失败了但显示未连接"，两个都不对）
      await this.teardown();
      this.updateStatus({
        phase: 'error',
        linkState: 'error',
        title: '链路失败',
        level: 'critical',
        reason,
        config,
        transportKind: kind,
        transportLabel: label,
      });
      return false;
    }

    const state = registry.session.state;
    const phase = LINK_PHASE_OF_STATE[state];
    if (phase === 'error') this.recordError(registry.session.stateReason ?? '链路打开后立刻报错', config);
    this.updateStatus({
      phase,
      linkState: state,
      ...describeLink(phase, state, registry.session.stateReason),
    });
    this.refreshStats();
    return phase !== 'error';
  }

  /**
   * 断开，但**保留**已归集的车辆与最后一帧状态。
   *
   * 断线不等于"忘掉刚才那架飞机"：复盘与排障都要用到"断之前最后看到的是什么"。
   * 这些历史值会不会被当成实时数据显示，由界面按 `phase` 决定
   * （见 `src/app/telemetry/TelemetryCards.tsx`），不靠把数据删掉来保证。
   */
  async disconnect(): Promise<void> {
    const registry = this.currentRegistry;
    if (registry === null) {
      this.updateStatus({ phase: 'disconnected', title: '未连接', level: 'neutral', reason: null });
      return;
    }

    try {
      await registry.close();
    } catch {
      /* 断开过程中的异常已经由链路状态报告，这里再抛一次只会把界面变成错误页 */
    }

    this.updateStatus({
      phase: 'disconnected',
      linkState: registry.session.state,
      title: '未连接',
      level: 'neutral',
      reason: '已断开（用户主动断开）',
    });
    this.refreshStats();
  }

  /** 彻底销毁：关闭链路、取消全部订阅、清空信号。销毁后不可再用（`connect` 会抛错） */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    await this.teardown();
    this.statusValue.clear();
    this.statsValue.clear();
    this.vehiclesValue.clear();
    this.primaryStateValue.clear();
    this.events.clear();
  }

  // -------------------------------------------------------------------------
  // 内部
  // -------------------------------------------------------------------------

  private handleLinkState(change: LinkStateEvent): void {
    const phase = LINK_PHASE_OF_STATE[change.state];
    if (phase === 'error') this.recordError(change.detail ?? '链路报告了错误，但底层没有给出原因', null);
    this.updateStatus({
      phase,
      linkState: change.state,
      ...describeLink(phase, change.state, change.detail),
    });
    // 链路状态变化是低频事件，顺手把统计刷新到最新（例如"静默多久了"在断开后不再有帧来驱动）
    this.refreshStats();
  }

  private handleVehicleState(event: VehicleStateEvent): void {
    if (event.primary) this.primaryStateValue.set(event.state);
    this.maybeRefreshStats();
  }

  private maybeRefreshStats(): void {
    const now = this.now();
    if (now - this.lastStatsAt < this.statsIntervalMs) return;
    this.refreshStats(now);
  }

  private refreshStats(at?: number): void {
    const registry = this.currentRegistry;
    this.statsValue.set(registry === null ? null : registry.session.stats());
    this.lastStatsAt = at ?? this.now();
  }

  private updateStatus(patch: Partial<Omit<LinkSnapshot, 'since'>> & { phase: LinkPhase }): void {
    const current = this.statusValue.get();
    const next: LinkSnapshot = {
      ...current,
      ...patch,
      since: patch.phase === current.phase ? current.since : this.now(),
    };
    if (sameStatus(current, next)) return;
    this.statusValue.set(next);
    this.events.emit('snapshot', next);
  }

  private recordError(message: string, config: LinkConfig | null): void {
    const report: LinkErrorReport = { message, at: this.now(), config: config ?? this.statusValue.get().config };
    this.lastErrorReport = report;
    this.events.emit('error', report);
  }

  /**
   * 失败原因。优先用会话给出的那句：它已经带上了"打开链路失败："这样的上下文，
   * 而且是从设备/浏览器真实报错翻译过来的。
   */
  private failureReason(error: unknown, registry: VehicleRegistry): string {
    const fromSession = registry.session.stateReason;
    if (fromSession !== undefined && fromSession.trim() !== '') return fromSession;
    return messageOf(error, '链路没有打开，并且底层没有给出原因');
  }

  private async teardown(): Promise<void> {
    // 先退订：清理由底层发出的事件不该再回来改状态机的快照
    for (const off of this.offs.splice(0)) off();

    if (this.statsTimer !== null) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }

    this.roster?.dispose();
    this.roster = null;

    const registry = this.currentRegistry;
    this.currentRegistry = null;
    this.currentTransport = null;

    if (registry !== null) {
      try {
        await registry.dispose();
      } catch {
        /* 清理失败不能覆盖真正的失败原因 */
      }
    }

    this.statsValue.set(null);
    this.vehiclesValue.set(EMPTY_VEHICLE_LIST);
    this.primaryStateValue.set(null);
  }
}

function messageOf(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  if (typeof error === 'string' && error.trim() !== '') return error;
  return fallback;
}

function sameStatus(a: LinkSnapshot, b: LinkSnapshot): boolean {
  return (
    a.phase === b.phase &&
    a.linkState === b.linkState &&
    a.title === b.title &&
    a.level === b.level &&
    a.reason === b.reason &&
    a.config === b.config &&
    a.transportKind === b.transportKind &&
    a.transportLabel === b.transportLabel &&
    a.since === b.since
  );
}
